import { Chip, Radio } from '@mantine/core';
import Select from '@xver/components/Atoms/Select/Select';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { XVerBaremetalPackageRender } from '../../../@types/launch';
import {
    PackageSortKey,
    parseCpuLabel,
    priceForPeriod,
    sortPackages,
    splitSpecLabel,
} from '../../../utils/baremetal';

type PaymentPeriod = 'weekly' | 'monthly' | 'yearly';

interface BaremetalPackageListProps {
    options: XVerBaremetalPackageRender[];
    selectedSlug?: string;
    onSelect: (slug: string) => void;
    paymentPeriod: PaymentPeriod;
    isLoading?: boolean;
}

const PERIODS: PaymentPeriod[] = ['weekly', 'monthly', 'yearly'];
const SORT_KEYS: PackageSortKey[] = ['priceAsc', 'priceDesc', 'memoryDesc', 'coresDesc'];
const ALL = '__all__';
const ROW_GRID =
    'grid grid-cols-[16px_minmax(0,1fr)_auto] md:grid-cols-[16px_minmax(0,2.2fr)_minmax(0,0.5fr)_minmax(0,0.9fr)_minmax(0,1.3fr)_minmax(0,0.7fr)_minmax(0,0.8fr)_176px] items-center gap-x-4';

function SpecCell({ label }: { label: string }) {
    const { value, detail } = splitSpecLabel(label);
    return (
        <span className="hidden md:flex flex-col min-w-0 text-sm text-fg">
            <span className="whitespace-nowrap">{value}</span>
            {detail && <span className="text-xs text-fg/68 truncate">{detail}</span>}
        </span>
    );
}

const PERIOD_SUFFIX_KEY = {
    weekly: 'deploymentSummaryCard.weeklySuffix',
    monthly: 'deploymentSummaryCard.monthlySuffix',
    yearly: 'deploymentSummaryCard.yearlySuffix',
} as const;

export default function BaremetalPackageList({
    options,
    selectedSlug,
    onSelect,
    paymentPeriod,
    isLoading = false,
}: BaremetalPackageListProps) {
    const { t } = useTranslation('components');
    const [cpuFilter, setCpuFilter] = useState(ALL);
    const [sortKey, setSortKey] = useState<PackageSortKey>('priceAsc');

    const rows = useMemo(
        () =>
            (options ?? []).map((pkg) => {
                const cpu = parseCpuLabel(pkg.cpu_label, pkg.core_label);
                return {
                    pkg,
                    cpu,
                    cpuTitle: cpu.count > 1 ? `${cpu.count}× ${cpu.model}` : cpu.model,
                };
            }),
        [options]
    );

    const models = useMemo(() => {
        const counts = new Map<string, number>();
        rows.forEach((r) => counts.set(r.cpu.model, (counts.get(r.cpu.model) ?? 0) + 1));
        return [...counts.entries()];
    }, [rows]);

    const visible = useMemo(() => {
        const sorted = sortPackages(
            rows.map((r) => r.pkg),
            sortKey,
            paymentPeriod
        );
        const bySlug = new Map(rows.map((r) => [r.pkg.slug, r]));
        return sorted
            .map((p) => bySlug.get(p.slug)!)
            .filter((r) => cpuFilter === ALL || r.cpu.model === cpuFilter);
    }, [rows, sortKey, paymentPeriod, cpuFilter]);

    const formatPrice = (pkg: XVerBaremetalPackageRender, period: PaymentPeriod) =>
        `฿${priceForPeriod({ ...pkg, payment_period: period }).toLocaleString()}${t(PERIOD_SUFFIX_KEY[period])}`;

    if (isLoading) {
        return (
            <div className="w-full flex flex-col gap-2" aria-busy="true">
                <span className="sr-only">{t('baremetalPackageList.loading')}</span>
                {[...Array(4)].map((_, i) => (
                    <div key={i} className="w-full h-14 rounded-md bg-surface-3 animate-pulse" />
                ))}
            </div>
        );
    }

    if (rows.length === 0) {
        return (
            <div className="w-full h-auto p-8 rounded-md border-[0.5px] border-line bg-surface-1 flex items-center justify-center">
                <p className="text-fg/68">{t('baremetalPackageList.noPackages')}</p>
            </div>
        );
    }

    return (
        <div className="w-full flex flex-col gap-3">
            <div className="w-full flex flex-col md:flex-row md:items-end justify-between gap-3">
                <div
                    role="group"
                    aria-label={t('baremetalPackageList.filters.cpu')}
                    className="flex flex-col gap-1.5 min-w-0"
                >
                    <span className="text-xs uppercase tracking-wider text-fg/68">
                        {t('baremetalPackageList.filters.cpu')}
                    </span>
                    <Chip.Group
                        multiple={false}
                        value={cpuFilter}
                        onChange={(v) => setCpuFilter(v || ALL)}
                    >
                        <div className="flex flex-row flex-wrap gap-1.5">
                            <Chip value={ALL} size="xs" radius="sm">
                                {t('baremetalPackageList.filters.all')} ({rows.length})
                            </Chip>
                            {models.map(([model, count]) => (
                                <Chip key={model} value={model} size="xs" radius="sm">
                                    {model} ({count})
                                </Chip>
                            ))}
                        </div>
                    </Chip.Group>
                </div>
                <Select
                    size="xs"
                    w={{ base: '100%', md: 200 }}
                    label={t('baremetalPackageList.sort.label')}
                    value={sortKey}
                    onChange={(v) => v && setSortKey(v as PackageSortKey)}
                    allowDeselect={false}
                    data={SORT_KEYS.map((key) => ({
                        value: key,
                        label: t(`baremetalPackageList.sort.${key}`),
                    }))}
                />
            </div>

            <div className="w-full rounded-md border-[0.5px] border-line overflow-hidden">
                <div
                    aria-hidden
                    className={`${ROW_GRID} hidden md:grid px-4 py-2.5 bg-surface-1 border-b-[0.5px] border-line text-xs font-semibold uppercase tracking-wider text-fg/68`}
                >
                    <span className="w-4" />
                    <span>{t('baremetalPackageList.headers.cpu')}</span>
                    <span>{t('baremetalPackageList.headers.cores')}</span>
                    <span>{t('baremetalPackageList.headers.memory')}</span>
                    <span>{t('baremetalPackageList.headers.storage')}</span>
                    <span>{t('baremetalPackageList.headers.network')}</span>
                    <span>{t('baremetalPackageList.headers.stock')}</span>
                    <span className="text-right">{t('baremetalPackageList.headers.price')}</span>
                </div>

                <div role="radiogroup" aria-label={t('baremetalPackageList.caption')}>
                    {visible.length === 0 && (
                        <p className="px-4 py-6 text-center text-sm text-fg/68">
                            {t('baremetalPackageList.noMatch')}
                        </p>
                    )}
                    {visible.map(({ pkg, cpu, cpuTitle }) => {
                        const isSelected = pkg.slug === selectedSlug;
                        const cores = cpu.cores ?? '—';
                        const price = formatPrice(pkg, paymentPeriod);
                        return (
                            <label
                                key={pkg.slug}
                                className={`${ROW_GRID} px-4 py-3 cursor-pointer border-b-[0.5px] border-line last:border-b-0 transition-colors hover:bg-surface-3`}
                                style={{
                                    backgroundColor: isSelected
                                        ? 'var(--color-selected-bg)'
                                        : 'var(--surface-2)',
                                    boxShadow: isSelected
                                        ? 'inset 3px 0 0 oklch(62.3% 0.214 259.815)'
                                        : undefined,
                                }}
                            >
                                <Radio
                                    size="xs"
                                    name="baremetal-package"
                                    value={pkg.slug}
                                    checked={isSelected}
                                    onChange={() => onSelect(pkg.slug)}
                                    aria-label={t('baremetalPackageList.select', {
                                        cpu: cpuTitle,
                                        cores,
                                        memory: pkg.memory_label,
                                        storage: pkg.storage_label,
                                        price,
                                    })}
                                />

                                <div className="flex flex-col min-w-0">
                                    <span className="flex flex-row flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-bold text-fg">
                                        {cpuTitle}
                                        {pkg.recommended && (
                                            <span
                                                className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium"
                                                style={{
                                                    backgroundColor: 'rgba(70, 132, 255, 0.15)',
                                                    color: 'var(--color-status-info)',
                                                }}
                                            >
                                                {t('baremetalPackageList.recommended')}
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-xs text-fg/68 md:hidden">
                                        {[
                                            cpu.cores &&
                                                `${cpu.cores} ${t('baremetalPackageList.headers.cores').toLowerCase()}`,
                                            pkg.memory_label,
                                            pkg.storage_label,
                                            pkg.network_label,
                                        ]
                                            .filter(Boolean)
                                            .join(' · ')}
                                    </span>
                                    <span className="text-xs text-fg/68 hidden md:inline">
                                        {cpu.clock}
                                    </span>
                                    <span className="text-xs text-fg/68 md:hidden">
                                        {t('baremetalPackageList.stockLeft', {
                                            count: pkg.quantity,
                                        })}
                                    </span>
                                </div>

                                <span className="hidden md:block text-sm text-fg">{cores}</span>
                                <SpecCell label={pkg.memory_label} />
                                <SpecCell label={pkg.storage_label} />
                                <span className="hidden md:block text-sm text-fg">
                                    {pkg.network_label}
                                </span>
                                <span className="hidden md:block text-sm text-fg/68">
                                    {t('baremetalPackageList.stockLeft', {
                                        count: pkg.quantity,
                                    })}
                                </span>

                                <div className="flex flex-col items-end text-nowrap">
                                    <span className="text-sm font-bold text-fg">{price}</span>
                                    <span className="hidden md:inline text-xs text-fg/68">
                                        {PERIODS.filter((p) => p !== paymentPeriod)
                                            .map((p) => formatPrice(pkg, p))
                                            .join(' · ')}
                                    </span>
                                </div>
                            </label>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
